// src/hooks/useDataFetch.js
import useSWR from "swr";
import { useDispatch } from "react-redux";
import apiClient from "../api/apiClient"; // Your Axios instance
import { useEffect } from "react";

// SWR fetcher function for API calls
const fetcher = (url) => apiClient.get(url).then((res) => res.data);

export const DEFAULT_ERROR_RETRY_COUNT = 3;

export const isAuthenticationOrAuthorizationError = (error) =>
  error?.response?.status === 401 || error?.response?.status === 403;

const getBoundedRetryCount = (retryCount) =>
  Number.isInteger(retryCount) && retryCount >= 0
    ? Math.min(retryCount, DEFAULT_ERROR_RETRY_COUNT)
    : DEFAULT_ERROR_RETRY_COUNT;

export const onDataFetchErrorRetry = (
  error,
  key,
  config,
  revalidate,
  { retryCount } = {}
) => {
  const currentRetryCount =
    Number.isInteger(retryCount) && retryCount > 0 ? retryCount : 1;
  const maximumRetryCount = getBoundedRetryCount(config.errorRetryCount);

  if (
    !key ||
    isAuthenticationOrAuthorizationError(error) ||
    currentRetryCount > maximumRetryCount
  ) {
    return;
  }

  setTimeout(
    () => revalidate({ retryCount: currentRetryCount, dedupe: true }),
    config.errorRetryInterval ?? 5000
  );
};

export const createDataFetchOptions = (options = {}) => ({
  keepPreviousData: true,
  revalidateOnReconnect: true,
  revalidateIfStale: true,
  revalidateOnFocus: false,
  revalidateOnMount: true,
  shouldRetryOnError: true,
  ...options,
  errorRetryCount: getBoundedRetryCount(options.errorRetryCount),
  onErrorRetry: onDataFetchErrorRetry,
});

const useDataFetch = (url, reduxAction = null, options = {}) => {
  const dispatch = useDispatch();

  // Use SWR for data fetching and caching
  const { data, error, isValidating, mutate } = useSWR(
    process.env.REACT_APP_API_BASE_URL + url,
    fetcher,
    createDataFetchOptions(options)
  );

  // Optionally sync data with Redux
  useEffect(() => {
    if (data && reduxAction) {
      dispatch(reduxAction(data));
    }
  }, [data, reduxAction, dispatch]);

  return {
    data,
    error,
    isLoading: !error && !data,
    isValidating,
    mutate,
  };
};

export default useDataFetch;
